/**
 * src/beta/BetaTestBar.jsx
 * Floating beta test bar that follows the user.
 * - Expandable/collapsible with a blinking BETA tab when collapsed.
 * - Step-by-step test instructions with thumbs up/down + comments per step.
 * - Final comment per test.
 * - Reset environment button.
 */
import { useState, useEffect, useCallback } from 'react';
import BETA_TESTS, { NEW_TEST_IDS } from './betaTests.js';

// ── Reset categories ──────────────────────────────────────────
// Mirrors ALLOWED_CATEGORIES in azure-functions/beta-environment-reset —
// keep in sync if a category is added/removed there.
const RESET_CATEGORIES = [
  { id: 'documents', label: 'Documents' },
  { id: 'risks', label: 'Risks & Opportunities' },
  { id: 'vendors', label: 'Vendors' },
  { id: 'suppliers', label: 'Suppliers / Customers / Delivery Agreements' },
  { id: 'assets', label: 'Assets & BIA' },
  { id: 'incidents', label: 'Incidents' },
  { id: 'employees', label: 'Employees' },
  { id: 'tasksAndAnalyses', label: 'Tasks / Gap Analyses / Threat Analyses' },
  { id: 'frameworkConfig', label: 'Framework & document-type configuration' },
  { id: 'billing', label: 'Billing settings' },
];

// ── localStorage helpers ──────────────────────────────────────

const STORAGE_KEY = 'seasuite_beta_progress';
const INTRO_SEEN_PREFIX = 'seasuite_beta_intro_seen_';

function loadProgress() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveProgress(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {}
}

function isIntroSeen(tenantId) {
  try {
    return !!localStorage.getItem(INTRO_SEEN_PREFIX + (tenantId || 'unknown'));
  } catch {
    return false;
  }
}

function markIntroSeen(tenantId) {
  try {
    localStorage.setItem(INTRO_SEEN_PREFIX + (tenantId || 'unknown'), '1');
  } catch {}
}

// ── Thumb button ──────────────────────────────────────────────

function ThumbButton({ type, active, onClick }) {
  const isUp = type === 'up';
  return (
    <button
      onClick={onClick}
      style={{
        width: 28,
        height: 28,
        borderRadius: 6,
        border: 'none',
        background: active
          ? isUp
            ? 'rgba(45,212,180,0.18)'
            : 'rgba(251,113,133,0.18)'
          : 'var(--bg3)',
        color: active ? (isUp ? '#2dd4bf' : '#fb7185') : 'var(--fg3)',
        cursor: 'pointer',
        fontSize: 14,
        lineHeight: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.15s',
      }}
      title={isUp ? 'Works well' : 'Has issues'}
    >
      {isUp ? '👍' : '👎'}
    </button>
  );
}

// ── Step row ──────────────────────────────────────────────────

function StepRow({ testId, step, stepIdx, progress, onUpdate }) {
  const key = `${testId}.${step.id}`;
  const data = progress[key] || {};
  const [commentOpen, setCommentOpen] = useState(false);
  const [comment, setComment] = useState(data.comment || '');
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    setComment(data.comment || '');
  }, [data.comment]);

  useEffect(() => {
    if (!justSaved) return;
    const t = setTimeout(() => setJustSaved(false), 1800);
    return () => clearTimeout(t);
  }, [justSaved]);

  const setRating = (val) => {
    onUpdate(key, { ...data, rating: data.rating === val ? null : val });
  };

  const saveComment = () => {
    onUpdate(key, { ...data, comment: comment.trim() || undefined });
    setJustSaved(true);
  };

  return (
    <div
      style={{
        padding: '10px 14px',
        borderBottom: '1px solid var(--border)',
        background: data.rating ? 'rgba(94,234,212,0.02)' : 'transparent',
      }}
    >
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <span
          style={{
            flexShrink: 0,
            width: 22,
            height: 22,
            borderRadius: 6,
            background: data.rating ? 'rgba(45,212,180,0.1)' : 'var(--bg3)',
            border: `1px solid ${data.rating ? 'rgba(45,212,180,0.2)' : 'var(--border)'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 10,
            fontWeight: 700,
            color: data.rating ? 'var(--accent)' : 'var(--fg3)',
            fontFamily: 'var(--font-mono)',
          }}
        >
          {stepIdx + 1}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, color: 'var(--fg)', lineHeight: 1.5 }}>{step.text}</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center' }}>
            <ThumbButton type="up" active={data.rating === 'up'} onClick={() => setRating('up')} />
            <ThumbButton
              type="down"
              active={data.rating === 'down'}
              onClick={() => setRating('down')}
            />
            <button
              onClick={() => setCommentOpen(!commentOpen)}
              style={{
                padding: '4px 10px',
                borderRadius: 6,
                fontSize: 10,
                fontWeight: 600,
                border: `1px solid ${data.comment ? 'rgba(56,189,248,0.3)' : 'var(--border)'}`,
                background: data.comment ? 'rgba(56,189,248,0.08)' : 'transparent',
                color: data.comment ? '#38bdf8' : 'var(--fg3)',
                cursor: 'pointer',
                fontFamily: 'var(--font-mono)',
              }}
            >
              {data.comment ? '✎ Comment' : '+ Comment'}
            </button>
          </div>
        </div>
      </div>

      {commentOpen && (
        <div style={{ marginTop: 8, marginLeft: 32 }}>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Your feedback on this step…"
            style={{
              width: '100%',
              minHeight: 60,
              padding: '8px 10px',
              borderRadius: 8,
              background: 'var(--bg)',
              border: '1px solid var(--border)',
              color: 'var(--fg)',
              fontSize: 12,
              fontFamily: 'var(--font-body)',
              resize: 'vertical',
              outline: 'none',
            }}
            onFocus={(e) => (e.target.style.borderColor = 'rgba(45,212,180,0.3)')}
            onBlur={(e) => (e.target.style.borderColor = 'var(--border)')}
          />
          <div
            style={{
              display: 'flex',
              gap: 6,
              marginTop: 6,
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            {justSaved && (
              <span
                role="status"
                aria-live="polite"
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: 'var(--accent)',
                  fontFamily: 'var(--font-mono)',
                  letterSpacing: '0.04em',
                  padding: '3px 8px',
                  borderRadius: 5,
                  background: 'rgba(45,212,180,0.12)',
                  border: '1px solid rgba(45,212,180,0.25)',
                  transition: 'opacity 0.2s',
                }}
              >
                ✓ Saved
              </span>
            )}
            <button
              onClick={() => setCommentOpen(false)}
              style={{
                padding: '4px 12px',
                borderRadius: 6,
                border: '1px solid var(--border)',
                background: 'transparent',
                color: 'var(--fg3)',
                fontSize: 11,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={saveComment}
              style={{
                padding: '4px 12px',
                borderRadius: 6,
                border: '1px solid rgba(45,212,180,0.3)',
                background: 'rgba(45,212,180,0.08)',
                color: 'var(--accent)',
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main BetaTestBar ──────────────────────────────────────────

export default function BetaTestBar({ tenantId, onResetEnvironment, onAnswersChange }) {
  const [expanded, setExpanded] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [progress, setProgress] = useState(loadProgress);
  const [finalComment, setFinalComment] = useState('');
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetSelection, setResetSelection] = useState(() =>
    Object.fromEntries(RESET_CATEGORIES.map((c) => [c.id, true]))
  );
  const [resetInFlight, setResetInFlight] = useState(false);
  const [resetError, setResetError] = useState(null);
  const [finalJustSaved, setFinalJustSaved] = useState(false);
  const [showIntro, setShowIntro] = useState(() => !isIntroSeen(tenantId));

  const dismissIntro = useCallback(() => {
    markIntroSeen(tenantId);
    setShowIntro(false);
  }, [tenantId]);

  useEffect(() => {
    if (!finalJustSaved) return;
    const t = setTimeout(() => setFinalJustSaved(false), 1800);
    return () => clearTimeout(t);
  }, [finalJustSaved]);

  // Load final comment when test changes
  useEffect(() => {
    if (selectedTest) {
      setFinalComment(progress[`${selectedTest}.final`]?.comment || '');
    }
  }, [selectedTest, progress]);

  const updateStep = useCallback(
    (key, data) => {
      setProgress((prev) => {
        const next = { ...prev, [key]: data };
        saveProgress(next);
        if (onAnswersChange) onAnswersChange(next);
        return next;
      });
    },
    [onAnswersChange]
  );

  const saveFinalComment = () => {
    if (!selectedTest) return;
    const key = `${selectedTest}.final`;
    setProgress((prev) => {
      const next = { ...prev, [key]: { comment: finalComment.trim() || undefined } };
      saveProgress(next);
      if (onAnswersChange) onAnswersChange(next);
      return next;
    });
    setFinalJustSaved(true);
  };

  const toggleResetCategory = (id) => {
    setResetSelection((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const selectedCategories = RESET_CATEGORIES.filter((c) => resetSelection[c.id]).map((c) => c.id);

  const handleReset = async () => {
    // Preserve beta test progress/answers — reset only the categories checked.
    if (selectedCategories.length === 0) return;
    setResetError(null);
    setResetInFlight(true);
    try {
      const result = await onResetEnvironment?.(selectedCategories);
      if (result && result.ok === false) {
        const failed = Object.entries(result.results || {})
          .filter(([, r]) => !r.ok)
          .map(([cat]) => cat);
        setResetError(`Reset partially failed: ${failed.join(', ') || 'unknown category'}`);
        return;
      }
      setShowResetConfirm(false);
    } catch (err) {
      setResetError(err.message || 'Reset failed');
    } finally {
      setResetInFlight(false);
    }
  };

  const currentTest = BETA_TESTS.find((t) => t.id === selectedTest);

  // Count completed steps per test (steps with a rating)
  const getTestProgress = (testId) => {
    const test = BETA_TESTS.find((t) => t.id === testId);
    if (!test) return { done: 0, total: 0 };
    const done = test.steps.filter((s) => progress[`${testId}.${s.id}`]?.rating).length;
    return { done, total: test.steps.length };
  };

  // ── Collapsed tab (blinking BETA icon) ──────────────────────

  if (!expanded) {
    return (
      <>
        <button
          onClick={() => {
            dismissIntro();
            setExpanded(true);
          }}
          className="beta-blink-tab"
          style={{
            position: 'fixed',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 9999,
            padding: '12px 8px',
            borderRadius: '8px 0 0 8px',
            background: 'linear-gradient(135deg, #7c3aed, #a78bfa)',
            border: '1px solid rgba(167,139,250,0.4)',
            borderRight: 'none',
            color: '#fff',
            fontWeight: 800,
            fontSize: 11,
            fontFamily: 'var(--font-mono)',
            cursor: 'pointer',
            letterSpacing: '0.1em',
            writingMode: 'vertical-lr',
            textOrientation: 'mixed',
            boxShadow: '0 4px 20px rgba(124,58,237,0.4)',
          }}
        >
          BETA
        </button>

        {showIntro && (
          <div
            role="dialog"
            aria-label="Beta test questions introduction"
            style={{
              position: 'fixed',
              right: 52,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 9999,
              width: 280,
              // The intro is an informational hint bubble — no backdrop, no dimming,
              // nothing that reads as modal — so it must never swallow a click meant
              // for the app underneath it. At a 1440px viewport its band covers a
              // Workbench's right-hand row actions and the Integrations detail
              // panel's controls, which looked to testers like dead buttons (#5642,
              // and #5533 on the privacy registers' delete controls).
              // Its own two buttons opt back in below.
              pointerEvents: 'none',
              padding: '14px 16px',
              background: 'linear-gradient(135deg, rgba(124,58,237,0.96), rgba(167,139,250,0.96))',
              border: '1px solid rgba(167,139,250,0.5)',
              borderRadius: 10,
              color: '#fff',
              boxShadow: '0 10px 40px rgba(124,58,237,0.45)',
              fontFamily: 'var(--font-body)',
              animation: 'betaPulse 2.5s ease-in-out infinite',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <span style={{ fontSize: 20, lineHeight: 1 }}>👋</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
                  Welcome, beta tester!
                </div>
                <div style={{ fontSize: 12, lineHeight: 1.45, opacity: 0.95 }}>
                  Your beta test questions live behind the <strong>BETA</strong> tab on the right.
                  Click it any time to rate steps and leave feedback.
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={dismissIntro}
                style={{
                  padding: '5px 12px',
                  borderRadius: 6,
                  border: '1px solid rgba(255,255,255,0.3)',
                  background: 'transparent',
                  color: '#fff',
                  fontSize: 11,
                  cursor: 'pointer',
                  pointerEvents: 'auto',
                }}
              >
                Got it
              </button>
              <button
                onClick={() => {
                  dismissIntro();
                  setExpanded(true);
                }}
                style={{
                  padding: '5px 12px',
                  borderRadius: 6,
                  border: '1px solid rgba(255,255,255,0.5)',
                  background: 'rgba(255,255,255,0.15)',
                  color: '#fff',
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: 'pointer',
                  pointerEvents: 'auto',
                }}
              >
                Open tests →
              </button>
            </div>
            {/* Arrow pointing to the BETA tab */}
            <div
              style={{
                position: 'absolute',
                right: -6,
                top: '50%',
                transform: 'translateY(-50%) rotate(45deg)',
                width: 12,
                height: 12,
                background:
                  'linear-gradient(135deg, rgba(124,58,237,0.96), rgba(167,139,250,0.96))',
                borderTop: '1px solid rgba(167,139,250,0.5)',
                borderRight: '1px solid rgba(167,139,250,0.5)',
              }}
            />
          </div>
        )}
      </>
    );
  }

  // ── Expanded bar ────────────────────────────────────────────

  return (
    <div
      style={{
        position: 'fixed',
        right: 0,
        top: 0,
        bottom: 0,
        width: 380,
        zIndex: 9998,
        background: 'var(--bg2)',
        borderLeft: '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '-4px 0 30px rgba(0,0,0,0.4)',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '16px 18px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              padding: '3px 10px',
              borderRadius: 6,
              background: 'linear-gradient(135deg, #7c3aed, #a78bfa)',
              color: '#fff',
              fontSize: 10,
              fontWeight: 800,
              fontFamily: 'var(--font-mono)',
              letterSpacing: '0.12em',
              animation: 'betaPulse 2s ease-in-out infinite',
            }}
          >
            BETA
          </span>
          <span className="display" style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg)' }}>
            Test Environment
          </span>
        </div>
        <button
          onClick={() => setExpanded(false)}
          style={{
            width: 28,
            height: 28,
            borderRadius: 6,
            border: '1px solid var(--border)',
            background: 'transparent',
            color: 'var(--fg3)',
            fontSize: 16,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          ×
        </button>
      </div>

      {/* Test environment banner */}
      <div
        style={{
          padding: '10px 18px',
          background: 'rgba(124,58,237,0.06)',
          borderBottom: '1px solid rgba(124,58,237,0.15)',
          fontSize: 11,
          color: '#a78bfa',
          fontFamily: 'var(--font-mono)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: '#a78bfa',
            animation: 'betaPulse 2s ease-in-out infinite',
          }}
        />
        Test Environment — All data is disposable
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {!selectedTest ? (
          /* ── Test list ── */
          <div style={{ padding: '16px 18px' }}>
            <div style={{ fontSize: 12, color: 'var(--fg2)', marginBottom: 16, lineHeight: 1.6 }}>
              Welcome to Tryggr beta testing! Select a test below to begin. Each test walks you
              through a specific feature — rate each step and leave comments to help us improve.
            </div>

            {/* ── Summary: new tests + incomplete reminder ── */}
            {(() => {
              const newUntouched = BETA_TESTS.filter(
                (t) => NEW_TEST_IDS.has(t.id) && getTestProgress(t.id).done === 0
              ).length;
              const incomplete = BETA_TESTS.filter((t) => {
                const { done, total } = getTestProgress(t.id);
                return done > 0 && done < total;
              }).length;
              if (newUntouched === 0 && incomplete === 0) return null;
              return (
                <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {newUntouched > 0 && (
                    <div
                      style={{
                        padding: '10px 14px',
                        borderRadius: 8,
                        background: 'rgba(251,191,36,0.08)',
                        border: '1px solid rgba(251,191,36,0.25)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                      }}
                    >
                      <span
                        style={{
                          padding: '2px 7px',
                          borderRadius: 5,
                          fontSize: 9,
                          fontWeight: 800,
                          background: '#fbbf24',
                          color: '#1a1a1a',
                          fontFamily: 'var(--font-mono)',
                          letterSpacing: '0.08em',
                        }}
                      >
                        NEW
                      </span>
                      <span style={{ fontSize: 12, color: '#fbbf24', fontWeight: 600 }}>
                        {newUntouched} new test{newUntouched > 1 ? 's' : ''} added — Assets, BIA,
                        Incidents, ROPA, Mgt Review & Integrations
                      </span>
                    </div>
                  )}
                  {incomplete > 0 && (
                    <div
                      style={{
                        padding: '10px 14px',
                        borderRadius: 8,
                        background: 'rgba(167,139,250,0.06)',
                        border: '1px solid rgba(167,139,250,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                      }}
                    >
                      <span style={{ fontSize: 14 }}>📋</span>
                      <span style={{ fontSize: 12, color: '#a78bfa', fontWeight: 600 }}>
                        {incomplete} incomplete test{incomplete > 1 ? 's' : ''} — keep going!
                      </span>
                    </div>
                  )}
                </div>
              );
            })()}

            {BETA_TESTS.map((test, i) => {
              const { done, total } = getTestProgress(test.id);
              const pct = total > 0 ? Math.round((done / total) * 100) : 0;
              const isNew = NEW_TEST_IDS.has(test.id);
              const isNewUntouched = isNew && done === 0;
              return (
                <button
                  key={test.id}
                  onClick={() => setSelectedTest(test.id)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '14px 16px',
                    background:
                      done === total && total > 0
                        ? 'rgba(45,212,180,0.04)'
                        : isNewUntouched
                          ? 'rgba(251,191,36,0.03)'
                          : 'transparent',
                    border: `1px solid ${
                      done === total && total > 0
                        ? 'rgba(45,212,180,0.15)'
                        : isNewUntouched
                          ? 'rgba(251,191,36,0.2)'
                          : 'var(--border)'
                    }`,
                    borderRadius: 10,
                    cursor: 'pointer',
                    marginBottom: 8,
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.borderColor = 'rgba(167,139,250,0.3)')
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.borderColor =
                      done === total && total > 0
                        ? 'rgba(45,212,180,0.15)'
                        : isNewUntouched
                          ? 'rgba(251,191,36,0.2)'
                          : 'var(--border)')
                  }
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <span
                      style={{
                        flexShrink: 0,
                        width: 24,
                        height: 24,
                        borderRadius: 7,
                        background:
                          done === total && total > 0 ? 'rgba(45,212,180,0.12)' : 'var(--bg3)',
                        border: `1px solid ${done === total && total > 0 ? 'rgba(45,212,180,0.25)' : 'var(--border)'}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 10,
                        fontWeight: 700,
                        color: done === total && total > 0 ? 'var(--accent)' : 'var(--fg3)',
                        fontFamily: 'var(--font-mono)',
                      }}
                    >
                      {done === total && total > 0 ? '✓' : i + 1}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)' }}>
                      {test.title}
                    </span>
                    {isNew && (
                      <span
                        style={{
                          padding: '1px 6px',
                          borderRadius: 4,
                          fontSize: 8,
                          fontWeight: 800,
                          background: done > 0 ? 'rgba(251,191,36,0.15)' : '#fbbf24',
                          color: done > 0 ? '#fbbf24' : '#1a1a1a',
                          fontFamily: 'var(--font-mono)',
                          letterSpacing: '0.06em',
                          lineHeight: '16px',
                        }}
                      >
                        NEW
                      </span>
                    )}
                  </div>
                  <div
                    style={{ fontSize: 11, color: 'var(--fg3)', lineHeight: 1.5, marginLeft: 34 }}
                  >
                    {test.description}
                  </div>
                  {done > 0 && (
                    <div style={{ marginLeft: 34, marginTop: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div
                          style={{ flex: 1, height: 3, borderRadius: 2, background: 'var(--bg4)' }}
                        >
                          <div
                            style={{
                              width: `${pct}%`,
                              height: '100%',
                              borderRadius: 2,
                              background: done === total ? 'var(--accent)' : '#a78bfa',
                              transition: 'width 0.3s',
                            }}
                          />
                        </div>
                        <span
                          style={{
                            fontSize: 10,
                            color: 'var(--fg3)',
                            fontFamily: 'var(--font-mono)',
                            flexShrink: 0,
                          }}
                        >
                          {done}/{total}
                        </span>
                      </div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        ) : (
          /* ── Selected test detail ── */
          <div>
            {/* Back + test title */}
            <div
              style={{
                padding: '14px 18px',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <button
                onClick={() => setSelectedTest(null)}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  border: '1px solid var(--border)',
                  background: 'transparent',
                  color: 'var(--fg3)',
                  fontSize: 14,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                ←
              </button>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg)' }}>
                  {currentTest?.title}
                </div>
                <div style={{ fontSize: 11, color: 'var(--fg3)', marginTop: 2 }}>
                  {currentTest?.description}
                </div>
              </div>
            </div>

            {/* Progress bar */}
            {currentTest &&
              (() => {
                const { done, total } = getTestProgress(selectedTest);
                const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                return (
                  <div
                    style={{
                      padding: '10px 18px',
                      borderBottom: '1px solid var(--border)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    <div style={{ flex: 1, height: 4, borderRadius: 2, background: 'var(--bg4)' }}>
                      <div
                        style={{
                          width: `${pct}%`,
                          height: '100%',
                          borderRadius: 2,
                          background: done === total ? 'var(--accent)' : '#a78bfa',
                          transition: 'width 0.3s',
                        }}
                      />
                    </div>
                    <span
                      style={{
                        fontSize: 10,
                        fontFamily: 'var(--font-mono)',
                        color: done === total ? 'var(--accent)' : 'var(--fg3)',
                        fontWeight: 600,
                      }}
                    >
                      {done}/{total} steps
                    </span>
                  </div>
                );
              })()}

            {/* Steps */}
            {currentTest?.steps.map((step, idx) => (
              <StepRow
                key={step.id}
                testId={selectedTest}
                step={step}
                stepIdx={idx}
                progress={progress}
                onUpdate={updateStep}
              />
            ))}

            {/* Final comment */}
            <div style={{ padding: '16px 18px', borderTop: '1px solid var(--border)' }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'var(--fg3)',
                  marginBottom: 8,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  fontFamily: 'var(--font-mono)',
                }}
              >
                Final Comment for This Test
              </div>
              <textarea
                value={finalComment}
                onChange={(e) => setFinalComment(e.target.value)}
                placeholder="Overall thoughts on this test…"
                style={{
                  width: '100%',
                  minHeight: 80,
                  padding: '10px 12px',
                  borderRadius: 8,
                  background: 'var(--bg)',
                  border: '1px solid var(--border)',
                  color: 'var(--fg)',
                  fontSize: 12,
                  fontFamily: 'var(--font-body)',
                  resize: 'vertical',
                  outline: 'none',
                }}
                onFocus={(e) => (e.target.style.borderColor = 'rgba(45,212,180,0.3)')}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--border)';
                  saveFinalComment();
                }}
              />
              <div
                style={{
                  marginTop: 6,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 8,
                }}
              >
                {finalJustSaved && (
                  <span
                    role="status"
                    aria-live="polite"
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: 'var(--accent)',
                      fontFamily: 'var(--font-mono)',
                      letterSpacing: '0.04em',
                      padding: '3px 8px',
                      borderRadius: 5,
                      background: 'rgba(45,212,180,0.12)',
                      border: '1px solid rgba(45,212,180,0.25)',
                    }}
                  >
                    ✓ Saved
                  </span>
                )}
                <button
                  onClick={saveFinalComment}
                  style={{
                    padding: '5px 14px',
                    borderRadius: 6,
                    border: '1px solid rgba(45,212,180,0.3)',
                    background: 'rgba(45,212,180,0.08)',
                    color: 'var(--accent)',
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Save Comment
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer — reset button */}
      <div
        style={{
          padding: '14px 18px',
          borderTop: '1px solid var(--border)',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {showResetConfirm ? (
          <div
            style={{
              padding: '12px 14px',
              borderRadius: 8,
              background: 'rgba(251,113,133,0.08)',
              border: '1px solid rgba(251,113,133,0.2)',
            }}
          >
            <div style={{ fontSize: 12, color: '#fb7185', fontWeight: 600, marginBottom: 8 }}>
              Reset environment data?
            </div>
            <div style={{ fontSize: 11, color: 'var(--fg3)', marginBottom: 10, lineHeight: 1.5 }}>
              Choose what to clear. Your beta test answers, ratings and comments, and your login,
              are always <strong style={{ color: 'var(--fg2)' }}>preserved</strong>.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
              {RESET_CATEGORIES.map((c) => (
                <label
                  key={c.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 11,
                    color: 'var(--fg2)',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!resetSelection[c.id]}
                    onChange={() => toggleResetCategory(c.id)}
                  />
                  {c.label}
                </label>
              ))}
            </div>
            {resetError && (
              <div style={{ fontSize: 11, color: '#fb7185', marginBottom: 10 }}>{resetError}</div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowResetConfirm(false);
                  setResetError(null);
                }}
                disabled={resetInFlight}
                style={{
                  padding: '6px 14px',
                  borderRadius: 6,
                  border: '1px solid var(--border)',
                  background: 'transparent',
                  color: 'var(--fg3)',
                  fontSize: 11,
                  cursor: resetInFlight ? 'default' : 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleReset}
                disabled={resetInFlight || selectedCategories.length === 0}
                style={{
                  padding: '6px 14px',
                  borderRadius: 6,
                  border: '1px solid rgba(251,113,133,0.4)',
                  background: 'rgba(251,113,133,0.12)',
                  color: '#fb7185',
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: resetInFlight || selectedCategories.length === 0 ? 'default' : 'pointer',
                  opacity: resetInFlight || selectedCategories.length === 0 ? 0.6 : 1,
                }}
              >
                {resetInFlight ? 'Resetting…' : 'Yes, Reset Selected'}
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowResetConfirm(true)}
            style={{
              width: '100%',
              padding: '10px 0',
              borderRadius: 8,
              border: '1px solid rgba(251,113,133,0.25)',
              background: 'rgba(251,113,133,0.06)',
              color: '#fb7185',
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: 'var(--font-mono)',
              letterSpacing: '0.04em',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(251,113,133,0.12)';
              e.currentTarget.style.borderColor = 'rgba(251,113,133,0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(251,113,133,0.06)';
              e.currentTarget.style.borderColor = 'rgba(251,113,133,0.25)';
            }}
          >
            ↺ Reset Environment
          </button>
        )}
      </div>
    </div>
  );
}
